import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import CustomSelect from "../../../../../components/Select/Select";
import {
  createDynamicCertificate,
  getDynamicCertificatesWoLoader,
} from "../../../../../redux/reducers/clinicSlice";
import {
  getPetAndAppointmentDetailsById,
  updateWeightByPetAndApntId,
} from "../../../../../redux/reducers/petSlice";
import { countryList } from "../../../../../util/arrayList";
import { getRightSaluation } from "../../../../../util/function";
import {
  findStandardTemplateByName,
  getCertificateGenerateFields,
} from "../../../Plan/certificateConstants";

const getLoggedInUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getLoggedInDoctorId = () => {
  const user = getLoggedInUser();
  return user?.doctorId ?? user?.id ?? user?.userId ?? null;
};

const toApiDateTime = (date = new Date()) => {
  const d = new Date(date);
  const pad = (n, len = 2) => String(n).padStart(len, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3,
  )}000`;
};

const toApiDate = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const getSelectValue = (value) => {
  if (value && typeof value === "object") return value?.name ?? value?.value;
  return value ?? "";
};

const getFieldText = (field, value) => {
  if (field?.type === "date") return toApiDate(value);
  if (field?.type === "vet" || field?.type === "country") {
    return value?.name ?? getSelectValue(value);
  }
  return getSelectValue(value);
};

const isMicrochipCertificate = (cert) =>
  findStandardTemplateByName(cert?.name)?.id === "std-microchip";

const AddToCertificateModal = ({
  open,
  onClose,
  certificate,
  procedure,
  doctorId,
}) => {
  const dispatch = useDispatch();
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const petDet = reqDet?.pet;
  const appDet = reqDet?.appointment;
  const petProfile = petAndApntData?.pet ?? petDet;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const vets = useMemo(() => {
    try {
      const reqData = JSON.parse(mandateDataRaw || "{}");
      return Array.isArray(reqData?.vets) ? reqData.vets : [];
    } catch {
      return [];
    }
  }, [mandateDataRaw]);
  const [values, setValues] = useState({});
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  const fields = useMemo(
    () => getCertificateGenerateFields(certificate?.name),
    [certificate?.name],
  );

  const vetOptions = useMemo(
    () =>
      vets.map((vet) => {
        const id = vet?.doctorId ?? vet?.id;
        const label =
          `${getRightSaluation(vets, id)}${vet?.name ?? ""}`.trim() ||
          String(vet?.name ?? "");
        return { name: label, value: id, doctorId: id };
      }),
    [vets],
  );

  const countryOptions = useMemo(
    () =>
      countryList.map((country) => ({
        name: country.name,
        value: country.name,
      })),
    [],
  );

  useEffect(() => {
    if (!open) return;
    setError(false);
    setSaving(false);
    const existingRfid = String(petProfile?.rfid ?? "").trim();
    setValues(
      isMicrochipCertificate(certificate) && existingRfid
        ? { microchipNo: existingRfid }
        : {},
    );
  }, [open, certificate?.name, procedure?.procedureId, petProfile?.rfid]);

  const handleFieldChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = () => {
    if (saving) return;
    if (fields.length > 0) {
      const missing = fields.some((field) => {
        const text = getFieldText(field, values[field.key]);
        return !String(text ?? "").trim();
      });
      if (missing) {
        setError(true);
        return;
      }
    }

    const extraFields = fields.reduce((acc, field) => {
      if (field.type === "vet") {
        const selected = values[field.key];
        acc[`${field.key}Id`] =
          selected?.doctorId ?? selected?.value ?? selected ?? null;
        acc[field.key] = selected?.name ?? "";
        return acc;
      }
      acc[field.key] = getFieldText(field, values[field.key]);
      return acc;
    }, {});

    const resolvedDoctorId = doctorId ?? getLoggedInDoctorId();
    const loggedInUser = getLoggedInUser();
    const matchedVet = vets.find(
      (vet) =>
        resolvedDoctorId != null &&
        String(vet?.doctorId ?? vet?.id) === String(resolvedDoctorId),
    );
    const doctorName =
      appDet?.doctorName ??
      matchedVet?.name ??
      loggedInUser?.name ??
      loggedInUser?.userName ??
      "";

    const payload = {
      certificateData: {
        title: certificate?.name ?? procedure?.procedureName ?? "Certificate",
        fields: {
          dateTime: toApiDateTime(new Date()),
          appointmentId:
            appDet?.appointmentId ?? procedure?.appointmentId ?? null,
          ...extraFields,
        },
      },
      petId: petDet?.petId ?? appDet?.petId ?? null,
      petName: petDet?.petName ?? petDet?.name ?? "",
      userId: petDet?.userId ?? appDet?.userId ?? null,
      userName: petDet?.userName ?? appDet?.userName ?? "",
      doctorId: resolvedDoctorId,
      doctorName,
      procedureId: procedure?.procedureId,
    };
    setSaving(true);
    dispatch(createDynamicCertificate(payload)).then((res) => {
      if (!res?.payload) {
        setSaving(false);
        return;
      }
      const petId = petProfile?.petId ?? petDet?.petId ?? appDet?.petId;
      dispatch(
        getDynamicCertificatesWoLoader(petId != null ? `?petId=${petId}` : ""),
      );

      const microchipNo = String(extraFields?.microchipNo ?? "").trim();
      const currentRfid = String(petProfile?.rfid ?? "").trim();
      const shouldUpdateRfid =
        isMicrochipCertificate(certificate) &&
        Boolean(microchipNo) &&
        Boolean(petId) &&
        microchipNo !== currentRfid;

      const finish = () => {
        setSaving(false);
        onClose();
      };

      if (!shouldUpdateRfid) {
        finish();
        return;
      }

      const appointmentId =
        appDet?.appointmentId ?? procedure?.appointmentId ?? null;
      if (!appointmentId) {
        finish();
        return;
      }

      const weight =
        petProfile?.weight ?? petDet?.weight ?? appDet?.weight ?? "";
      dispatch(
        updateWeightByPetAndApntId({
          url: `/${appointmentId}/pet/${petId}/weight?weight=${weight}&rfid=${encodeURIComponent(
            microchipNo,
          )}`,
          skipToast: true,
        }),
      ).then((updateRes) => {
        if (updateRes?.payload) {
          dispatch(
            getPetAndAppointmentDetailsById({
              petId,
              url: `?appointmentId=${appointmentId}`,
            }),
          );
        }
        finish();
      });
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header={
        fields.length > 0
          ? `Complete ${certificate?.name ?? "Certificate"}`
          : "Add to Certificate"
      }
      modal
      modalWidth={40}
    >
      {fields.length > 0 ? (
        <Box px={1} pt={1} pb={2}>
          {fields.map((field) => {
            const list =
              field.type === "country"
                ? countryOptions
                : field.type === "vet"
                  ? vetOptions
                  : (field.options ?? []);
            const isSearchSelect = field.type === "country";
            const selectedVetId =
              values[field.key]?.doctorId ?? values[field.key]?.value ?? "";
            const selectedCountry =
              field.type === "country"
                ? (countryOptions.find(
                    (country) =>
                      country.name ===
                      (values[field.key]?.name ?? values[field.key]),
                  ) ?? null)
                : null;
            return (
              <Box key={field.key} mb={2}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#757575",
                    letterSpacing: "0.5px",
                    mb: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  {field.label}
                </Typography>
                {field.type === "select" ||
                field.type === "country" ||
                field.type === "vet" ? (
                  <CustomSelect
                    list={list}
                    value={
                      field.type === "vet"
                        ? selectedVetId
                        : field.type === "country"
                          ? selectedCountry
                          : getSelectValue(values[field.key]) || ""
                    }
                    handleChange={(e, option) => {
                      if (field.type === "vet") {
                        const selectedId = e?.target?.value;
                        const selected =
                          vetOptions.find(
                            (vet) =>
                              String(vet?.doctorId ?? vet?.value) ===
                              String(selectedId),
                          ) ?? null;
                        handleFieldChange(field.key, selected);
                        return;
                      }
                      if (field.type === "country") {
                        handleFieldChange(field.key, option ?? null);
                        return;
                      }
                      handleFieldChange(field.key, e?.target?.value);
                    }}
                    error={
                      error &&
                      !String(
                        getFieldText(field, values[field.key]) ?? "",
                      ).trim()
                    }
                    search={isSearchSelect}
                    fullWidth
                    select={!isSearchSelect}
                  />
                ) : (
                  <CustomTextField
                    fullWidth
                    value={
                      field.type === "date"
                        ? values[field.key] || null
                        : (values[field.key] ?? "")
                    }
                    handleChange={(e) =>
                      handleFieldChange(field.key, e?.target?.value)
                    }
                    multiline={field.type === "textarea"}
                    mobileDate={field.type === "date"}
                    error={
                      error &&
                      !String(
                        getFieldText(field, values[field.key]) ?? "",
                      ).trim()
                    }
                  />
                )}
              </Box>
            );
          })}
          <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
            <div>
              <CustomButton
                text="Add to Certificate"
                smallBtn
                onClick={onSubmit}
              />
            </div>
          </Box>
        </Box>
      ) : (
        <>
          <div className="flex-center">
            <Typography className="txt-semi-bold fs16 text-align-center mv20">
              Add this procedure to {certificate?.name ?? "the certificate"}?
            </Typography>
          </div>
          <div className="flex-row-ali-cen flex-center pb20">
            <div>
              <CustomButton text="Yes" smallBtn onClick={onSubmit} />
            </div>
            <div className="ml20">
              <CustomButton text="No" smallBtn whiteBtn onClick={onClose} />
            </div>
          </div>
        </>
      )}
    </CustomModal>
  );
};

export default AddToCertificateModal;
